import { useState, useCallback, useRef, useEffect } from "react";

import { Container } from "@material-ui/core";

import Api from "lib/api/httpRequestHelper";

import FilterCard from "components/_refactor/filter-card";
import HeaderClientes from "components/_refactor/headers/clients";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";

import UtilsFncs from "utils/UtilsFncs";

import Filters from "./components/filters";
import TablePrePedidos from "./components/table";
import PrePedidoDetailDrawer from "./components/pre-pedido-detail-drawer";
import fetchPrePedidosForExport from "./utils/fetchPrePedidosForExport";
import exportPrePedidosToXlsx from "./utils/exportPrePedidosXlsx";
import exportSinglePrePedido from "./utils/exportSinglePrePedido";
import { PRE_PEDIDOS_EXPORT_MAX } from "./utils/pre-pedidos-export-constants";
import "./pre-pedidos.scss";

const EMPTY_FILTERS = {
  vitrineName: "",
  clienteName: "",
  cnpj: "",
  dataPrePedidoInicio: null,
  dataPrePedidoFim: null,
};

export default function PrePedidosVitrineB2b() {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedDetalhe, setSelectedDetalhe] = useState(null);
  const [pedidosList, setPedidosList] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 25,
    totalSize: 0,
  });
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(null);

  const filtersRef = useRef(filters);
  const paginationRef = useRef(pagination);

  filtersRef.current = filters;
  paginationRef.current = pagination;

  const snackbar = useSnackbar();

  const fetchPedidos = useCallback(
    async (pageOverride, pageSizeOverride) => {
      setIsLoading(true);

      const currentFilters = filtersRef.current;
      const page = pageOverride ?? paginationRef.current.page ?? 0;
      const pageSize = pageSizeOverride ?? paginationRef.current.pageSize ?? 25;

      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
      });

      if (currentFilters.vitrineName?.trim()) {
        params.set("nome", currentFilters.vitrineName.trim());
      }
      if (currentFilters.clienteName?.trim()) {
        params.set("nomeCliente", currentFilters.clienteName.trim());
      }
      if (currentFilters.cnpj?.trim()) {
        params.set("cnpj", currentFilters.cnpj.trim());
      }
      if (currentFilters.dataPrePedidoInicio) {
        params.set(
          "dataPrePedidoInicio",
          UtilsFncs.ConvertDateFormat(
            currentFilters.dataPrePedidoInicio,
            "-",
            "-",
          ),
        );
      }
      if (currentFilters.dataPrePedidoFim) {
        params.set(
          "dataPrePedidoFim",
          UtilsFncs.ConvertDateFormat(
            currentFilters.dataPrePedidoFim,
            "-",
            "-",
          ),
        );
      }

      try {
        const response = await Api.get(
          "virtual-showcase-b2b-list-pre-pedidos",
          "showcaseB2B",
          `?${params.toString()}`,
          true,
        );
        const result = response?.Result?.Pedidos || response?.Result?.pedidos;
        setPagination({
          totalSize: response?.Result?.TotalRegistro || 0,
          page: response?.Result?.Pagina ?? page,
          pageSize,
          currentPage: response?.Result?.Pagina ?? page,
          currentSizePerPage: pageSize,
        });
        setPedidosList(Array.isArray(result) ? result : []);
      } catch {
        snackbar.showMessage("Falha ao buscar pré-pedidos. Tente novamente.", {
          severity: "error",
        });
        setPedidosList([]);
      } finally {
        setIsLoading(false);
      }
    },
    [snackbar],
  );

  const handleSearch = () => {
    setHasSearched(true);
    setPagination((prev) => ({ ...prev, page: 0 }));
    fetchPedidos(0);
  };

  const handlePageChange = (page, pageSize) => {
    setPagination((prev) => ({
      ...prev,
      page: page || 0,
      pageSize: pageSize || 25,
    }));
    if (hasSearched) {
      fetchPedidos(page || 0, pageSize || 25);
    }
  };

  const handleViewDetails = useCallback(
    async (idPedido) => {
      setLoadingDetails(true);
      setDetailOpen(true);
      setSelectedDetalhe(null);

      try {
        const response = await Api.get(
          "virtual-showcase-b2b-pre-pedido-detalhe",
          "showcaseB2B",
          `?idVitrineVirtualB2bPedido=${idPedido}`,
          true,
        );
        setSelectedDetalhe(response?.Result || null);
      } catch {
        snackbar.showMessage("Falha ao carregar detalhes do pré-pedido.", {
          severity: "error",
        });
        setDetailOpen(false);
      } finally {
        setLoadingDetails(false);
      }
    },
    [snackbar],
  );

  useEffect(() => {
    setHasSearched(true);
    fetchPedidos(0);
  }, [fetchPedidos]);

  const handleExportRow = useCallback(
    async (row) => {
      try {
        await exportSinglePrePedido(row);
        snackbar.showMessage(
          "Relatório XLSX do pré-pedido gerado com sucesso.",
          {
            severity: "success",
          },
        );
      } catch {
        snackbar.showMessage(
          "Falha ao exportar o pré-pedido. Tente novamente.",
          { severity: "error" },
        );
      }
    },
    [snackbar],
  );

  const handleExportXlsx = async () => {
    setExporting(true);
    setExportProgress({ phase: "list", loaded: 0, total: null });
    try {
      const { pedidos, detalhes, totalRegistro, truncated } =
        await fetchPrePedidosForExport(filters, {
          onProgress: setExportProgress,
        });

      if (pedidos.length === 0) {
        snackbar.showMessage(
          "Nenhum pré-pedido encontrado para exportar com os filtros atuais.",
          { severity: "warning" },
        );
        return;
      }

      exportPrePedidosToXlsx({ detalhes });

      if (truncated) {
        snackbar.showMessage(
          `Relatório limitado a ${PRE_PEDIDOS_EXPORT_MAX} pré-pedidos (${totalRegistro} no filtro). Refine os filtros para exportar o restante.`,
          { severity: "warning" },
        );
      } else {
        snackbar.showMessage("Relatório XLSX gerado com sucesso.", {
          severity: "success",
        });
      }
    } catch {
      snackbar.showMessage(
        "Falha ao gerar o relatório. Tente novamente ou refine os filtros.",
        { severity: "error" },
      );
    } finally {
      setExporting(false);
      setExportProgress(null);
    }
  };

  return (
    <>
      <HeaderClientes
        section="Pré-pedidos Vitrine B2B"
        subsection="Pré-Pedido"
      />
      <Container maxWidth={false} style={{ marginTop: "-4.5rem" }}>
        <FilterCard
          handleSearch={handleSearch}
          disabled={isLoading}
          searching={isLoading}
        >
          <Filters
            isLoading={isLoading}
            filters={filters}
            setFilters={setFilters}
          />
        </FilterCard>
        {/* <PrePedidosExportToolbar
          totalRegistros={hasSearched ? pagination.totalSize : null}
          exporting={exporting}
          exportProgress={exportProgress}
          onExport={handleExportXlsx}
          disabled={isLoading || !hasSearched}
        /> */}
        <TablePrePedidos
          data={pedidosList}
          loading={isLoading}
          totalSize={pagination.totalSize}
          currentPage={pagination.page}
          currentSizePerPage={pagination.pageSize}
          onPageChange={handlePageChange}
          loadingDetails={loadingDetails}
          handleViewDetails={handleViewDetails}
          handleExportRow={handleExportRow}
          exportDisabled={exporting}
        />
      </Container>

      <PrePedidoDetailDrawer
        open={detailOpen}
        onClose={() => {
          setDetailOpen(false);
          setSelectedDetalhe(null);
        }}
        detalhe={selectedDetalhe}
        loading={loadingDetails}
      />
    </>
  );
}
